import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { userAPI, authAPI } from '../services/api';
import { getAcquisition } from '../utils/acquisition';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Access token kept in memory (never persisted) — the strongest place we
  // can put it short of HttpOnly cookies. The legacy/long-lived token is
  // persisted to sessionStorage (was localStorage until 2026-05-14) so it
  // clears when the tab closes — shrinks the exposure window if a device is
  // left signed in. HttpOnly cookies are the real fix for XSS-based token
  // theft and are tracked separately as post-launch work.
  const accessTokenRef = useRef(null);
  // Legacy token for backward compatibility
  const [legacyToken, setLegacyToken] = useState(null);
  const [activeMode, setActiveMode] = useState(null);

  // Token refresh timer and retry tracking
  const refreshTimerRef = useRef(null);
  const refreshRetryCount = useRef(0);
  const MAX_REFRESH_RETRIES = 3;
  // Note: inactivity-based auto-logout removed — sessions managed by token expiry only

  // Schedule token refresh before expiry - with retry logic
  const scheduleTokenRefresh = useCallback((expiresIn) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Refresh 2 minutes before expiry to give more buffer
    const refreshTime = Math.max((expiresIn - 120) * 1000, 60000);
    refreshTimerRef.current = setTimeout(async () => {
      try {
        const result = await authAPI.refreshToken();
        if (result.access_token) {
          accessTokenRef.current = result.access_token;
          setLegacyToken(result.token);
          refreshRetryCount.current = 0; // Reset retries on success
          scheduleTokenRefresh(result.expires_in || 900);
        }
      } catch (error) {
        refreshRetryCount.current += 1;
        console.log(`Token refresh failed (attempt ${refreshRetryCount.current}/${MAX_REFRESH_RETRIES})`);

        if (refreshRetryCount.current < MAX_REFRESH_RETRIES) {
          // Retry after 30 seconds instead of logging out immediately
          console.log('Retrying token refresh in 30 seconds...');
          scheduleTokenRefresh(30 + 120); // 30s + the 120s buffer offset
        } else {
          // Refresh has failed repeatedly — the session is genuinely gone.
          // (The legacy-token fallback was removed 2026-06-11; auth is now
          //  exclusively the signed access/refresh cookies.)
          console.log('Max refresh retries reached, logging out');
          logout();
        }
      }
    }, refreshTime);
  }, []);

  // On mount, check for existing session and validate with backend
  useEffect(() => {
    const initAuth = async () => {
      // One-shot cleanup: purge every legacy auth artefact from web storage.
      // The unsigned `user_<id>` token was removed server-side 2026-06-11, so
      // any stored authToken is now a dead, unusable string. Auth is carried
      // exclusively by the HttpOnly access/refresh cookies.
      try {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('user');
      } catch { /* private mode / storage disabled — fall through */ }

      // No optimistic hydration from local storage anymore. The user
      // object (roles, email, name) lived in sessionStorage which is
      // XSS-readable. Now we fetch it fresh via /api/users/me on every
      // mount. Brief loading flash is the price; the security win is
      // that no client-side cache of identity fields exists.
      try {
        const refreshResult = await authAPI.refreshToken().catch(() => null);
        if (refreshResult && refreshResult.access_token) {
          // The new access_token also lives in an HttpOnly cookie. We
          // keep it in memory too for the Authorization-header fallback
          // path, which the backend still accepts during the cutover.
          accessTokenRef.current = refreshResult.access_token;

          const userData = await userAPI.getCurrentUser(refreshResult.access_token);
          setUser(userData);
          setActiveMode(userData.activeMode || userData.roles?.[0] || 'guest');
          scheduleTokenRefresh(refreshResult.expires_in || 900);
        } else {
          // No valid refresh token — definitively not logged in.
          setUser(null);
          setActiveMode(null);
        }
      } catch (error) {
        console.log('Auth bootstrap failed');
        setUser(null);
        setActiveMode(null);
      }
      setLoading(false);
    };
    
    initAuth();
    
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [scheduleTokenRefresh]);

  // Cross-tab logout sync was removed when auth moved to sessionStorage.
  // The browser only fires `storage` events for localStorage changes; with
  // sessionStorage each tab has its own isolated session, so there's nothing
  // to sync — new tabs already require their own login.
  // Inactivity-based auto-logout was previously removed too; session lifetime
  // is now managed entirely by token expiry (access token + refresh token).

  // Login with backend.
  // The server may respond with a 2FA challenge instead of tokens:
  //   { requires2FA: true,      userId, message }  -> user has 2FA enabled
  //   { requires2FASetup: true, userId, message }  -> admin must enrol first
  // In both cases we return the result un-persisted so LoginPage can render
  // the second step. The token + user blob only land in state once
  // complete2FALogin succeeds.
  const login = useCallback(async (email, password) => {
    const result = await userAPI.login({ email, password });

    if (result?.requires2FA || result?.requires2FASetup) {
      return result;
    }

    accessTokenRef.current = result.access_token;
    setLegacyToken(result.token);

    setUser(result.user);
    setActiveMode(result.user.activeMode || result.user.roles?.[0] || 'guest');

    // access_token + refresh_token are now in HttpOnly cookies set by the
    // backend; we no longer persist any token to sessionStorage. The user
    // blob stays as UI state (not a credential).
    // User object intentionally not cached — read fresh from /me on
    // mount to avoid XSS-readable identity data in client storage.

    if (result.expires_in) {
      scheduleTokenRefresh(result.expires_in);
    }

    return result;
  }, [scheduleTokenRefresh]);

  // Second step of a 2FA login. Called after the user submits their TOTP
  // (or backup) code. Persists the auth result on success exactly like a
  // plain password login.
  const complete2FALogin = useCallback(async (userId, code) => {
    const result = await userAPI.login2FA(userId, code);
    accessTokenRef.current = result.access_token;
    setLegacyToken(result.token);
    setUser(result.user);
    setActiveMode(result.user.activeMode || result.user.roles?.[0] || 'guest');
    // User object intentionally not cached — read fresh from /me on
    // mount to avoid XSS-readable identity data in client storage.
    if (result.expires_in) {
      scheduleTokenRefresh(result.expires_in);
    }
    return result;
  }, [scheduleTokenRefresh]);

  // Internal: persist the result of an auth call (login / signup / SSO)
  // into context state + localStorage. Centralised here so any new
  // auth path can flow through it without duplicating the four
  // moving parts (access token, legacy token, user, refresh schedule).
  const persistAuthResult = useCallback((result) => {
    accessTokenRef.current = result.access_token;
    setLegacyToken(result.token);
    setUser(result.user);
    setActiveMode(result.user.activeMode || result.user.roles?.[0] || 'guest');
    // access_token + refresh_token are now in HttpOnly cookies set by the
    // backend; we no longer persist any token to sessionStorage. The user
    // blob stays as UI state (not a credential).
    // User object intentionally not cached — read fresh from /me on
    // mount to avoid XSS-readable identity data in client storage.
    if (result.expires_in) {
      scheduleTokenRefresh(result.expires_in);
    }
  }, [scheduleTokenRefresh]);

  // Sign in (or sign up) with a Google ID token. Backend issues the
  // exact same JWT shape as a password login, so we reuse the same
  // persistence path.
  const loginWithGoogle = useCallback(async (idToken) => {
    const { default: axios } = await import('axios');
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const { data } = await axios.post(`${API_URL}/api/auth/google`, {
      credential: idToken,
      // Partner tracking-link tags; the backend uses them only if this
      // creates a new account.
      acquisition: getAcquisition(),
    });
    if (data.requires2FA || data.requires2FASetup) {
      // Surface to the caller; the LoginPage handles the 2FA step
      // already for password logins.
      return data;
    }
    persistAuthResult(data);
    return data;
  }, [persistAuthResult]);

  // Sign in (or sign up) with an Apple ID token. The optional `user`
  // payload carries the name on first-ever sign-in only.
  const loginWithApple = useCallback(async (idToken, appleUserBlob) => {
    const { default: axios } = await import('axios');
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const { data } = await axios.post(`${API_URL}/api/auth/apple`, {
      credential: idToken,
      user: appleUserBlob || null,
      acquisition: getAcquisition(),
    });
    if (data.requires2FA || data.requires2FASetup) {
      return data;
    }
    persistAuthResult(data);
    return data;
  }, [persistAuthResult]);

  // Signup with backend
  const signup = useCallback(async (userData) => {
    const result = await userAPI.signup(userData);
    
    // Store access token in memory
    accessTokenRef.current = result.access_token;
    setLegacyToken(result.token);
    
    setUser(result.user);
    setActiveMode(result.user.activeMode || result.user.roles?.[0] || 'guest');
    
    // Store legacy token for backward compatibility
    // access_token + refresh_token are now in HttpOnly cookies set by the
    // backend; we no longer persist any token to sessionStorage. The user
    // blob stays as UI state (not a credential).
    // User object intentionally not cached — read fresh from /me on
    // mount to avoid XSS-readable identity data in client storage.
    
    // Schedule refresh
    if (result.expires_in) {
      scheduleTokenRefresh(result.expires_in);
    }
    
    return result;
  }, [scheduleTokenRefresh]);

  // Logout — clears local state instantly, revokes backend token in background
  const logout = useCallback(() => {
    // Grab token before clearing
    const token = accessTokenRef.current || legacyToken;

    // Clear local state IMMEDIATELY — no waiting on network
    accessTokenRef.current = null;
    setLegacyToken(null);
    setUser(null);
    setActiveMode(null);

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('user');

    // Fire-and-forget: revoke refresh token on backend (don't block UI)
    if (token) {
      authAPI.logout(token).catch(() => {});
    }
  }, [legacyToken]);

  // Check authentication
  const isAuthenticated = useCallback(() => {
    return user !== null && (accessTokenRef.current !== null || legacyToken !== null);
  }, [user, legacyToken]);

  // Check if user HAS host role (persisted in DB)
  const hasHostRole = useCallback(() => {
    if (!user) return false;
    const roles = user.roles || [];
    return roles.includes('host');
  }, [user]);

  // Check if user HAS guest role
  const hasGuestRole = useCallback(() => {
    if (!user) return false;
    const roles = user.roles || [];
    return roles.includes('guest');
  }, [user]);

  // Check current active mode
  const isInHostMode = useCallback(() => {
    return activeMode === 'host' && hasHostRole();
  }, [activeMode, hasHostRole]);

  const isInGuestMode = useCallback(() => {
    return activeMode === 'guest';
  }, [activeMode]);

  // Switch mode - persists to backend
  const switchMode = useCallback(async (mode) => {
    const token = accessTokenRef.current || legacyToken;
    if (!user || !token) return false;
    const roles = user.roles || [];
    
    if (mode === 'host' && !roles.includes('host')) {
      console.warn('User does not have host role');
      return false;
    }
    
    if (mode === 'guest' || mode === 'host') {
      try {
        await userAPI.updateActiveMode(mode, token);
        setActiveMode(mode);
        // React state only — user object not persisted to session
        // storage; identity fields stay out of XSS reach.
        const updatedUser = { ...user, activeMode: mode };
        setUser(updatedUser);
        return true;
      } catch (error) {
        console.error('Failed to update mode:', error);
        return false;
      }
    }
    return false;
  }, [user, legacyToken]);

  // Complete host onboarding - calls backend to persist host role
  const completeHostOnboarding = useCallback(async (onboardingData) => {
    const token = accessTokenRef.current || legacyToken;
    if (!user || !token) return { success: false };
    
    try {
      const result = await userAPI.completeHostOnboarding(onboardingData, token);
      if (result.success && result.user) {
        setUser(result.user);
        setActiveMode('host');
        // User object intentionally not cached — read fresh from /me on
    // mount to avoid XSS-readable identity data in client storage.
      }
      return result;
    } catch (error) {
      console.error('Host onboarding failed:', error);
      throw error;
    }
  }, [user, legacyToken]);

  // Get the auth token for API calls (prefer access token, fall back to legacy)
  const getAuthToken = useCallback(() => {
    return accessTokenRef.current || legacyToken;
  }, [legacyToken]);

  // Refresh user data from backend
  const refreshUser = useCallback(async () => {
    const token = accessTokenRef.current || legacyToken;
    if (!token) return null;
    try {
      const userData = await userAPI.getCurrentUser(token);
      setUser(userData);
      setActiveMode(userData.activeMode || userData.roles?.[0] || 'guest');
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  }, [legacyToken]);

  // Check if this is a company account
  const isCompanyAccount = useCallback(() => {
    return user?.accountType === 'company';
  }, [user]);

  const value = {
    user,
    token: accessTokenRef.current || legacyToken,
    login,
    complete2FALogin,
    loginWithGoogle,
    loginWithApple,
    signup,
    logout,
    isAuthenticated,
    hasHostRole,
    hasGuestRole,
    isInHostMode,
    isInGuestMode,
    activeMode,
    switchMode,
    completeHostOnboarding,
    getAuthToken,
    refreshUser,
    isCompanyAccount,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
